import { Check } from 'lucide-react';
import type { ToolbarAppsConfig, ToolbarNotification } from './notificationTypes';
import { formatNotificationDisplayDate, removeRecurrentNotificationsThatAlreadyPassed, resolveNotificationImage, resolveNotificationName } from './notificationModel';
import { cn } from '../lib/utils';

type ToolbarNotificationsListProps = {
    items: ToolbarNotification[];
    appsConfig: ToolbarAppsConfig | undefined;
    translate: (key: string) => string;
    resolveAsset: (path: string) => string;
    className?: string;
};

export function ToolbarNotificationsList({ items, appsConfig, translate, resolveAsset, className }: ToolbarNotificationsListProps) {
    const filtered = removeRecurrentNotificationsThatAlreadyPassed(items);

    return (
        <section className={cn('overflow-hidden bg-white text-gray-900 shadow-sm', className)}>
            <ul className='divide-y divide-gray-100'>
                {filtered.map((el) => {
                    const imageSrc = resolveNotificationImage(el, appsConfig, resolveAsset);
                    const name = resolveNotificationName(el, appsConfig, translate);
                    const unread = !el.read_at;

                    return (
                        <li key={String(el.id)}>
                            <a
                                href={el.link}
                                target='_blank'
                                rel='noreferrer'
                                data-notification-id={String(el.id)}
                                className={cn('group relative z-0 flex gap-3 px-4 py-3.5 transition-[background-color,box-shadow,ring] duration-200 hover:z-[2] hover:bg-gray-50/90 hover:ring-gray-200/90', unread ? 'bg-white' : 'bg-gray-50/40 opacity-95')}
                            >
                                <div className='relative shrink-0'>
                                    <div className={cn('flex size-11 shrink-0 items-center justify-center rounded-2xl bg-indigo-50 ring-1 ring-inset ring-indigo-100/80')}>
                                        {imageSrc ? <img width={26} height={26} src={imageSrc} alt={name} className='size-[26px] object-contain' /> : <Check className='size-5 text-indigo-600' strokeWidth={2.5} aria-hidden />}
                                    </div>
                                    {unread && <span className='absolute -right-0.5 -top-0.5 size-2.5 rounded-full border-2 border-white bg-purple-500' aria-label='Não lida' />}
                                </div>

                                <div className='min-w-0 flex-1 pt-0.5'>
                                    <p className='text-[13px] font-semibold leading-snug text-gray-900 line-clamp-2 group-hover:line-clamp-none'>{el.title}</p>
                                    <p className='mt-0.5 text-sm leading-snug text-gray-600 line-clamp-2 group-hover:line-clamp-none'>{name}</p>
                                    <p className='mt-1.5 text-xs text-gray-400'>{formatNotificationDisplayDate(el.broadcasted_at)}</p>
                                </div>
                            </a>
                        </li>
                    );
                })}
            </ul>
        </section>
    );
}
